import React, { useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput, FlatList, Image, Dimensions, ScrollView, Platform } from 'react-native';
import PrimaryButton from '../components/PrimaryButton';
import colors from '../theme/colors';
import { useTableContext } from '../context/TableContext';

const { width } = Dimensions.get('window');

const floatingShadowStyles = Platform.OS === 'web'
  ? { boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.25)' }
  : {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.25,
      shadowRadius: 4,
      elevation: 5
    };

const AVAILABLE_PRODUCTS = [
  {
    id: '1',
    nome: 'Guaraná',
    preco: 5.50,
    estoque: 24,
    descricao: 'Refrigerante guaraná lata 350ml',
    imagem: 'https://images.unsplash.com/photo-1510627498534-cf7e9002facc?auto=format&fit=crop&w=200&q=60'
  },
  {
    id: '2',
    nome: 'Fanta',
    preco: 5.50,
    estoque: 18,
    descricao: 'Fanta laranja lata 350ml',
    imagem: 'https://images.unsplash.com/photo-1524594154908-edd0e0cddc16?auto=format&fit=crop&w=200&q=60'
  },
  {
    id: '3',
    nome: 'Cerveja Skol',
    preco: 15.00,
    estoque: 40,
    descricao: 'Skol long neck 275ml',
    imagem: 'https://images.unsplash.com/photo-1509223180465-0c16baebb5a0?auto=format&fit=crop&w=200&q=60'
  },
  {
    id: '4',
    nome: 'Cerveja Heineken',
    preco: 18.00,
    estoque: 32,
    descricao: 'Heineken long neck 330ml',
    imagem: 'https://images.unsplash.com/photo-1516455590571-18256e5bb9ff?auto=format&fit=crop&w=200&q=60'
  },
  {
    id: '5',
    nome: 'Cerveja Budweiser',
    preco: 16.00,
    estoque: 28,
    descricao: 'Budweiser long neck 330ml',
    imagem: 'https://images.unsplash.com/photo-1527169402691-feff5539e52c?auto=format&fit=crop&w=200&q=60'
  },
  {
    id: '6',
    nome: 'Cerveja Brahma',
    preco: 14.00,
    estoque: 35,
    descricao: 'Brahma Duplo Malte 350ml',
    imagem: 'https://images.unsplash.com/photo-1571907486588-9937bd132a95?auto=format&fit=crop&w=200&q=60'
  },
  {
    id: '7',
    nome: 'Chopp',
    preco: 25.00,
    estoque: 15,
    descricao: 'Chopp artesanal 500ml',
    imagem: 'https://images.unsplash.com/photo-1516455590571-18256e5bb9ff?auto=format&fit=crop&w=200&q=60'
  },
  {
    id: '8',
    nome: 'Refrigerante',
    preco: 8.00,
    estoque: 20,
    descricao: 'Refrigerante sabores variados 1L',
    imagem: 'https://images.unsplash.com/photo-1581578731557-9303c7e95b9d?auto=format&fit=crop&w=200&q=60'
  }
];

export default function SellProductScreen({ onNavigate, screenParams }) {
  const { addProductsToTable, tables } = useTableContext();
  const { tableId, isKioskMode, kioskFunctions } = screenParams || {};
  
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [feedback, setFeedback] = useState(null);
  const [showCart, setShowCart] = useState(false);
  const [showCartDetails, setShowCartDetails] = useState(false);
  const [search, setSearch] = useState(''); // Search state
  
  const isMobile = width < 768;
  const mobileColumns = 3;
  const desktopColumns = 4;
  const numColumns = isMobile ? mobileColumns : desktopColumns;
  const table = tables.find((t) => t.id === tableId);

  const handleBack = () => {
    onNavigate('OpenTables', { isKioskMode, kioskFunctions });
  };

  const filteredProducts = AVAILABLE_PRODUCTS.filter(p =>
    p.nome.toLowerCase().includes(search.toLowerCase())
  );

  const productCardWidth = useMemo(() => {
    const horizontalPadding = isMobile ? 24 : 32; // padding horizontal do container
    const gap = 12;
    const columns = isMobile ? mobileColumns : desktopColumns;
    const totalGap = gap * (columns - 1);
    return (width - horizontalPadding - totalGap) / columns;
  }, [isMobile]);

  const handleAddProduct = (product) => {
    if (feedback) setFeedback(null);
    const existing = selectedProducts.find(p => p.id === product.id);
    if (existing) {
      setSelectedProducts(
        selectedProducts.map(p =>
          p.id === product.id ? { ...p, quantidade: p.quantidade + 1 } : p
        )
      );
    } else {
      const newSelectedProducts = [...selectedProducts, { ...product, quantidade: 1 }];
      setSelectedProducts(newSelectedProducts);
      
      // Mostrar carrinho ao adicionar primeiro produto
      if (selectedProducts.length === 0) {
        setShowCart(true);
      }
    }
  };

  const handleRemoveProduct = (productId) => {
    const newSelectedProducts = selectedProducts.filter(p => p.id !== productId);
    setSelectedProducts(newSelectedProducts);
    
    // Esconder carrinho se não houver mais produtos
    if (newSelectedProducts.length === 0) {
      setShowCart(false);
      setShowCartDetails(false);
    }
    
    if (feedback) setFeedback(null);
  };

  const handleDecreaseQty = (productId) => {
    const newSelectedProducts = selectedProducts
        .map(p =>
          p.id === productId ? { ...p, quantidade: p.quantidade - 1 } : p
        )
        .filter(p => p.quantidade > 0);
    
    setSelectedProducts(newSelectedProducts);
    
    // Esconder carrinho se não houver mais produtos
    if (newSelectedProducts.length === 0) {
      setShowCart(false);
      setShowCartDetails(false);
    }
    
    if (feedback) setFeedback(null);
  };

  const total = selectedProducts.reduce((sum, p) => sum + (p.preco * p.quantidade), 0);

  const handleConfirmSale = () => {
    if (!table) {
      setFeedback({ type: 'error', message: 'Mesa não encontrada. Volte e tente novamente.' });
      return;
    }
    if (selectedProducts.length === 0) {
      setFeedback({ type: 'error', message: 'Selecione pelo menos um produto antes de confirmar.' });
      return;
    }
    
    addProductsToTable(table.id, selectedProducts);
    setFeedback({ type: 'success', message: `Venda confirmada para mesa ${table.mesa}. Total: R$ ${total.toFixed(2)}` });
    setSelectedProducts([]);
    setShowCart(false);
    setShowCartDetails(false);
    setTimeout(() => {
      setFeedback(null);
      onNavigate('OpenTables', { isKioskMode, kioskFunctions });
    }, 1200);
  };

  const renderProduct = ({ item }) => (
    <TouchableOpacity
      style={[
        styles.productCard,
        isMobile ? styles.productCardMobile : styles.productCardDesktop,
        { width: productCardWidth }
      ]}
      onPress={() => handleAddProduct(item)}
    >
      <Image source={{ uri: item.imagem }} style={styles.productImage} />
      <Text style={styles.productName} numberOfLines={1} ellipsizeMode="tail">{item.nome}</Text>
      <Text style={styles.productDescription} numberOfLines={2} ellipsizeMode="tail">{item.descricao}</Text>
      {isMobile ? (
        <View style={styles.productMetaMobile}>
          <Text style={styles.productStockMobile}>Estoque: {item.estoque}</Text>
          <Text style={styles.productPrice}>R$ {item.preco.toFixed(2)}</Text>
        </View>
      ) : (
        <View style={styles.productMeta}>
          <Text style={styles.productPrice}>R$ {item.preco.toFixed(2)}</Text>
          <Text style={styles.productStock}>Estoque: {item.estoque}</Text>
        </View>
      )}
      <Text style={styles.productAdd}>Adicionar</Text>
    </TouchableOpacity>
  );

  const renderSelectedProduct = ({ item }) => (
    <View style={styles.selectedProductItem}>
      <View style={styles.selectedProductInfo}>
        <Text style={styles.selectedProductName}>{item.nome}</Text>
        <Text style={styles.selectedProductPrice}>R$ {(item.preco * item.quantidade).toFixed(2)}</Text>
      </View>
      <View style={styles.qtyControls}>
        <TouchableOpacity onPress={() => handleDecreaseQty(item.id)}>
          <Text style={styles.qtyButton}>-</Text>
        </TouchableOpacity>
        <Text style={styles.qty}>{item.quantidade}</Text>
        <TouchableOpacity onPress={() => handleAddProduct(item)}>
          <Text style={styles.qtyButton}>+</Text>
        </TouchableOpacity>
      </View>
      <TouchableOpacity onPress={() => handleRemoveProduct(item.id)}>
        <Text style={styles.removeButton}>✕</Text>
      </TouchableOpacity>
    </View>
  );

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={handleBack}>
          <Text style={styles.backButton}>{'<'}</Text>
        </TouchableOpacity>
        <Text style={styles.title}>Vender - Mesa {table ? table.mesa : '?'}</Text>
        <View style={{ width: 40 }} />
      </View>

      <View style={styles.searchContainer}>
        <TextInput
          style={styles.searchInput}
          placeholder="Pesquisar produto..."
          placeholderTextColor={colors.muted}
          value={search}
          onChangeText={setSearch}
        />
      </View>

      <ScrollView 
        style={styles.content}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={true}
      >
        {!table && (
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>Mesa não encontrada</Text>
            <PrimaryButton
              title="Voltar"
              onPress={handleBack}
              style={styles.errorButton}
            />
          </View>
        )}

        {feedback && (
          <View style={styles.feedbackOverlay}>
            <View
              style={[
                styles.feedbackModal,
                feedback.type === 'success' ? styles.feedbackSuccess : styles.feedbackError
              ]}
            >
              <Text style={styles.feedbackText}>{feedback.message}</Text>
            </View>
          </View>
        )}

        <View style={styles.productsSection}>
          <Text style={styles.sectionTitle}>Produtos Disponíveis</Text>
          <FlatList
            data={filteredProducts}
            keyExtractor={item => item.id}
            renderItem={renderProduct}
            numColumns={numColumns}
            columnWrapperStyle={isMobile ? styles.productRowMobile : styles.productRow}
            contentContainerStyle={[
              isMobile ? styles.productGridMobile : styles.productGrid,
              { paddingBottom: showCart ? 100 : 20 } // Espaço extra quando carrinho está visível
            ]}
            scrollEnabled={false}
          />
        </View>
      </ScrollView>

      {/* Carrinho Flutuante */}
      {showCart && (
        <View style={styles.floatingCart}>
          <TouchableOpacity 
            style={styles.cartButton}
            onPress={() => setShowCartDetails(!showCartDetails)}
          >
            <View style={styles.cartIcon}>
              <Text style={styles.cartIconText}>🛒</Text>
              <View style={styles.cartBadge}>
                <Text style={styles.cartBadgeText}>{selectedProducts.length}</Text>
              </View>
            </View>
            <Text style={styles.cartTotal}>R$ {total.toFixed(2)}</Text>
          </TouchableOpacity>

          {showCartDetails && (
            <View style={styles.cartDetails}>
              <Text style={styles.cartDetailsTitle}>Seus Produtos</Text>
              <FlatList
                data={selectedProducts}
                keyExtractor={item => item.id}
                renderItem={renderSelectedProduct}
                style={styles.cartList}
                scrollEnabled={true}
              />
              <View style={styles.cartTotalSection}>
                <Text style={styles.cartTotalLabel}>Total:</Text>
                <Text style={styles.cartTotalValue}>R$ {total.toFixed(2)}</Text>
              </View>
              <PrimaryButton
                title="Adicionar à Mesa"
                onPress={handleConfirmSale}
                style={styles.cartConfirmButton}
              />
            </View>
          )}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF'
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.border
  },
  backButton: {
    fontSize: 24,
    color: colors.accent,
    fontWeight: '700'
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.text
  },
  searchContainer: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.border
  },
  searchInput: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 14
  },
  content: {
    flex: 1
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingVertical: 16
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  },
  errorText: {
    fontSize: 16,
    color: colors.error,
    marginBottom: 16
  },
  errorButton: {
    width: 120
  },
  productsSection: {
    marginBottom: 16
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.text,
    marginBottom: 12
  },
  productGrid: {
    gap: 12
  },
  productGridMobile: {
    gap: 12
  },
  productRow: {
    justifyContent: 'space-between',
    marginBottom: 12
  },
  productRowMobile: {
    justifyContent: 'space-between',
    marginBottom: 12
  },
  productCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 12,
    borderWidth: 1,
    borderColor: colors.border,
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column'
  },
  productCardDesktop: {
    minHeight: 200
  },
  productCardMobile: {
    minHeight: 180
  },
  productImage: {
    width: '100%',
    height: 80,
    borderRadius: 8,
    marginBottom: 8
  },
  productName: {
    fontSize: 14,
    fontWeight: '700',
    color: colors.text,
    textAlign: 'center',
    marginBottom: 4
  },
  productDescription: {
    fontSize: 11,
    color: colors.muted,
    textAlign: 'center',
    marginBottom: 8,
    lineHeight: 14
  },
  productMeta: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginBottom: 8
  },
  productMetaMobile: {
    width: '100%',
    flexDirection: 'column',
    alignItems: 'flex-end',
    marginBottom: 8,
    gap: 4
  },
  productPrice: {
    fontSize: 12,
    fontWeight: '700',
    color: colors.primary,
    flexShrink: 0
  },
  productStock: {
    fontSize: 11,
    color: colors.accent,
    fontWeight: '600',
    marginLeft: 'auto',
    textAlign: 'right'
  },
  productStockMobile: {
    fontSize: 11,
    color: colors.accent,
    fontWeight: '600',
    textAlign: 'right'
  },
  productAdd: {
    marginTop: 'auto',
    alignSelf: 'stretch',
    textAlign: 'center',
    fontSize: 12,
    fontWeight: '700',
    color: '#FFFFFF',
    backgroundColor: colors.primary,
    paddingVertical: 8,
    borderRadius: 6
  },
  selectedSection: {
    backgroundColor: '#F8F9FA',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16
  },
  selectedProductItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: colors.border
  },
  selectedProductInfo: {
    flex: 1
  },
  selectedProductName: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text
  },
  selectedProductPrice: {
    fontSize: 12,
    color: colors.primary,
    fontWeight: '700'
  },
  qtyControls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginHorizontal: 8
  },
  qtyButton: {
    width: 24,
    height: 24,
    borderRadius: 4,
    backgroundColor: colors.primary,
    color: '#FFFFFF',
    textAlign: 'center',
    lineHeight: 24,
    fontWeight: '700'
  },
  qty: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.text,
    minWidth: 20,
    textAlign: 'center'
  },
  removeButton: {
    fontSize: 18,
    color: '#DC2626',
    fontWeight: '700'
  },
  totalSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 10,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderTopWidth: 2,
    borderTopColor: colors.primary,
    marginBottom: 12
  },
  totalLabel: {
    fontSize: 14,
    fontWeight: '700',
    color: colors.text
  },
  totalValue: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.primary
  },
  confirmButton: {
    marginBottom: 16
  },
  feedbackOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000
  },
  feedbackModal: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    margin: 20,
    maxWidth: 300
  },
  feedbackSuccess: {
    borderLeftWidth: 4,
    borderLeftColor: '#10B981'
  },
  feedbackError: {
    borderLeftWidth: 4,
    borderLeftColor: '#DC2626'
  },
  feedbackText: {
    fontSize: 14,
    color: colors.text,
    textAlign: 'center'
  },
  // Estilos do Carrinho Flutuante
  floatingCart: {
    position: 'absolute',
    bottom: 20,
    right: 20,
    zIndex: 1000
  },
  cartButton: {
    backgroundColor: colors.primary,
    borderRadius: 50,
    paddingVertical: 12,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.25)',
    elevation: 5
  },
  cartIcon: {
    position: 'relative'
  },
  cartIconText: {
    fontSize: 20
  },
  cartBadge: {
    position: 'absolute',
    top: -8,
    right: -8,
    backgroundColor: '#DC2626',
    borderRadius: 10,
    minWidth: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center'
  },
  cartBadgeText: {
    color: '#FFFFFF',
    fontSize: 10,
    fontWeight: '700'
  },
  cartTotal: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '700'
  },
  cartDetails: {
    position: 'absolute',
    bottom: 80,
    right: 0,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    width: 300,
    maxHeight: 400,
    ...floatingShadowStyles
  },
  cartDetailsTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.text,
    marginBottom: 12
  },
  cartList: {
    maxHeight: 200,
    marginBottom: 12
  },
  cartTotalSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    marginBottom: 12
  },
  cartTotalLabel: {
    fontSize: 14,
    fontWeight: '700',
    color: colors.text
  },
  cartTotalValue: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.primary
  },
  cartConfirmButton: {
    marginBottom: 0
  }
});
